import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { acceptTerms } from '../../api/auth';
import { haptics } from '../../telegram';
import { OFFER_TEXT } from './offerText';

/** Consider the offer "read" a bit before the exact bottom (px). */
const READ_THRESHOLD = 24;

/** Top-level section headers: "5. Права и обязанности…" / "Приложение №1 …",
 *  but not clause numbers like "5.1. …". */
const isHeading = (line: string) =>
  (/^\d+\.\s+\D/.test(line) && !/^\d+\.\d/.test(line)) || line.startsWith('Приложение');

const offerBlocks = OFFER_TEXT.split('\n')
  .map((l) => l.trim())
  .filter(Boolean)
  .map((line, i) => ({ line, heading: isHeading(line), key: i }));

function Check({ on }: { on: boolean }) {
  return (
    <span className={`terms-box${on ? ' terms-box--on' : ''}`}>
      {on && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth={3}>
          <path d="M5 12l5 5 9-11" />
        </svg>
      )}
    </span>
  );
}

export function TermsScreen() {
  const { markTermsAccepted } = useAuth();
  const [offer, setOffer] = useState(false);
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Continue is locked until the user scrolls the offer to the end.
  const [readEnd, setReadEnd] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Degenerate case: content fits without scrolling ⇒ nothing to scroll through.
    const el = cardRef.current;
    if (el && el.scrollHeight <= el.clientHeight + READ_THRESHOLD) setReadEnd(true);
  }, []);

  const onCardScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!readEnd && el.scrollTop + el.clientHeight >= el.scrollHeight - READ_THRESHOLD) {
      setReadEnd(true);
      haptics.tap();
    }
  };

  const confirm = async () => {
    if (!offer || !age || !readEnd) return;
    setBusy(true);
    setError(null);
    try {
      await acceptTerms();
      haptics.success();
      markTermsAccepted(); // unmounts this screen
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить согласие');
      haptics.error();
      setBusy(false);
    }
  };

  return (
    <div className="auth terms">
      <div className="auth-title">Публичная оферта</div>
      <div className="auth-sub">Ознакомьтесь с условиями участия и подтвердите согласие.</div>

      <div className="terms-card" ref={cardRef} onScroll={onCardScroll}>
        {offerBlocks.map(({ line, heading, key }) =>
          heading ? <h4 key={key}>{line}</h4> : <p key={key}>{line}</p>,
        )}
      </div>

      <button className="terms-check" onClick={() => setOffer((v) => !v)}>
        <Check on={offer} />
        <span>
          Я принимаю условия публичной оферты и согласие на обработку персональных данных
        </span>
      </button>
      <button className="terms-check" onClick={() => setAge((v) => !v)}>
        <Check on={age} />
        <span>Мне исполнилось 18 лет</span>
      </button>

      {error && <div className="field-error auth-error">{error}</div>}

      <button className="btn" disabled={!offer || !age || !readEnd || busy} onClick={confirm}>
        {busy
          ? 'Сохраняем…'
          : readEnd
            ? 'Принять и продолжить'
            : 'Пролистайте оферту до конца'}
      </button>
    </div>
  );
}
